// 会议主持人 (src/office/meethost.ts): who hosts each 会议室, its lock and a 认领主持人 vote, as the office says
// ({t:"meet_host", rooms}); the host's actions ({t:"meet_host", op}); what happens to me: 主持人把你静音了 (a notice with
// 取消静音: I may unmute myself), 请离开 (my otter walks out of the door, as 离开会议室), a locked door, a vote's result;
// and the vote card 「X 想当主持人」 同意 / 反对 (public/members.js addCard, with its countdown). The panel, the tiles and
// the name tags read state() / isHost() (public/meeting.js, app.js tagIcons: a crown).
import { t, has } from '/i18n.js';
import { put } from '/icons.js';

const CSS = `#mhnote{position:fixed;left:50%;top:calc(76px + var(--st,0px));transform:translateX(-50%);z-index:62;display:none;box-sizing:border-box;width:min(340px,calc(100vw - 24px));
  background:#fff8e7;color:#1b1410;border:3px solid #1b1410;border-radius:14px;box-shadow:0 3px 0 #1b1410;padding:10px 12px;font:14px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif)}
#mhnote .t{font-weight:700;display:flex;gap:6px;align-items:center} #mhnote .sub{font-size:12px;color:#776c5c;margin:4px 0 8px}
#mhnote .bt{display:flex;gap:6px} #mhnote button{font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:4px 12px;background:#fff;cursor:pointer}
#mhnote button.go{background:#FDD03B}`;
const NOTE_MS = 12_000;

/**
 * @param o {send, toast, me(), invitedTo(room): I hold an invite into it (public/meetinvite.js), members (public/members.js: addCard, dropCard), mic: {muted(), set(on)}, leave(): 离开会议室
 *   (public/meeting.js), zoneAt(x, y), guest: a guest or visitor (never host, no claim, no vote)}
 */
export function initMeetHost(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  let rooms = new Map(), skew = 0; // room id -> {host, hostName, locked, vote}; the office's clock minus mine
  const note = document.createElement('div'); note.id = 'mhnote'; document.body.append(note);
  let noteTimer = 0;
  const myRoom = () => { const me = o.me(); const z = me && o.zoneAt(me.x, me.y); return z?.meeting ? z.id : null; };

  function showMuted(by) {
    const head = put(document.createElement('div'), 'mic_off', t('meetHost.muted.title'));
    head.className = 't';
    const sub = document.createElement('div'); sub.className = 'sub'; sub.textContent = t('meetHost.muted.sub', { name: by || t('meetHost.host') });
    const un = put(document.createElement('button'), 'mic', t('meetHost.muted.unmute')); un.className = 'go';
    un.onclick = () => { o.mic.set(false); hide(); };
    const ok = document.createElement('button'); ok.textContent = t('meetHost.muted.ok'); ok.onclick = hide;
    const bt = document.createElement('div'); bt.className = 'bt'; bt.append(un, ok);
    note.replaceChildren(head, sub, bt); note.style.display = 'block';
    clearTimeout(noteTimer); noteTimer = setTimeout(hide, NOTE_MS);
  }
  function hide() { note.style.display = 'none'; clearTimeout(noteTimer); }

  /** The vote card, if I'm asked (in the room, one of those who haven't answered, not the claimer). */
  let carded = null;
  function voteCard() {
    const me = o.me(), r = myRoom() ? rooms.get(myRoom()) : null, v = r?.vote;
    const ask = !o.guest && me && v && v.by !== me.id && v.voters.includes(me.id);
    if (!ask) { if (carded) { o.members.dropCard(carded); carded = null; } return; }
    const id = 'mhvote:' + v.id;
    if (carded === id) return;
    if (carded) o.members.dropCard(carded);
    carded = id;
    const answer = (yes) => { carded = null; o.send({ t: 'meet_host', op: 'vote', vote: v.id, yes }); };
    o.members.addCard({ id, icon: 'crown', text: t('meetHost.vote.card', { name: v.name }), anytime: true, prio: 5,
      yes: t('meetHost.vote.yes'), yesIcon: 'check', no: t('meetHost.vote.no'), noIcon: 'close', expires: v.ends - skew,
      left: (ms) => t('meetHost.vote.left', { n: Math.ceil(ms / 1000) }), accept: () => answer(true), decline: () => answer(false) });
  }

  function msg(m) {
    if (m.t === 'meet_host') {
      if (!Array.isArray(m.rooms)) return;
      if (typeof m.now === 'number') skew = m.now - Date.now();
      rooms = new Map(m.rooms.filter((r) => r && typeof r.room === 'string').map((r) => [r.room, r]));
      voteCard();
    } else if (m.t === 'meet_host_ev') {
      if (m.ev === 'muted') { if (!o.mic.muted()) o.mic.set(true); showMuted(m.by); }
      else if (m.ev === 'removed') { o.toast(t('meetHost.removed', { name: m.by || t('meetHost.host') }), null, 'door'); o.leave(); }
      else if (m.ev === 'locked') { if (Date.now() - toldAt > 3000) { toldAt = Date.now(); o.toast(t('meetHost.lockedOut'), null, 'door'); } }
      else if (m.ev === 'vote' && typeof m.name === 'string') o.toast(t(m.ok ? 'meetHost.vote.passed' : m.cancelled ? 'meetHost.vote.cancelled' : 'meetHost.vote.failed', { name: m.name }), null, 'crown');
    } else if (m.t === 'meet_host_err') {
      const k = `meetHost.err.${m.code}`;
      o.toast(has(k) ? t(k, { s: Math.ceil((m.retry_ms || 0) / 1000) }) : t('meetHost.err.other'));
    }
  }

  /** Room `id` as the office last said: {host (peer id), hostName, locked, vote} or null. */
  const state = (id) => rooms.get(id) ?? null;
  /** Does p (a peer, or me) host the 会议室 it stands in? */
  function isHost(p) {
    if (!p) return false;
    const z = o.zoneAt(p.x, p.y); if (!z?.meeting) return false;
    return rooms.get(z.id)?.host === p.id;
  }
  const act = (op, extra = {}) => o.send({ t: 'meet_host', op, ...extra });
  let toldAt = 0;
  /** The mover's gate (deck/move.js mayEnter, asked before public/call.js's): a step into a 会议室 its host locked, from
   *  outside it, without an invite into it, isn't taken (the office would refuse it anyway: Movement mayMove); it says
   *  so, as the office's refusal does. So a 通话 isn't ended for a door that won't open. */
  function mayEnter(x0, y0, x, y) {
    const z = o.zoneAt(x, y);
    if (!z?.meeting || !rooms.get(z.id)?.locked || o.zoneAt(x0, y0)?.id === z.id || o.invitedTo?.(z.id)) return true;
    const now = Date.now();
    if (now - toldAt > 3000) { toldAt = now; o.toast(t('meetHost.lockedOut'), null, 'door'); }
    return false;
  }
  return {
    msg, state, isHost, mayEnter,
    /** Am I the host of the room I'm in? */
    mine: () => { const me = o.me(); return !!me && isHost(me); },
    canClaim: () => !o.guest && !!myRoom(),
    mute: (id) => act('mute', { id }), muteAll: () => act('mute_all'), remove: (id) => act('remove', { id }),
    lock: (on) => act('lock', { on }), claim: () => act('claim'),
    /** A 会议室's plaque on the map: 「已锁门」 while locked. */
    roomTag: (zid) => (rooms.get(zid)?.locked ? t('meetHost.tag.locked') : null),
    /** A new connection: the office sends the rooms again in its onJoin. */
    welcome() { hide(); },
  };
}
